import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Text,
  VStack,
  HStack,
  LoadingSpinner,
  Card,
  Highlight,
  ScrollView,
  IconButton,
} from '@adc/parallax-component-library';
import {
  IconBannersInfo,
  IconBannersWarning,
  IconCheckCircle,
  IconDownload,
} from '@adc/parallax-icons';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import { supportsFileDownload } from '../../utilities/downloadFile';
import { DownloadStatus } from '../../constants';
import { DownloadParams, DownloadState } from '../../hooks/useStudyDownload';
import { ProgressBar } from '../progressBar/ProgressBar';
import { ConfirmationModal } from './ConfirmationModal';
import './Modal.scss';

export interface DownloadModalBaseProps {
  isOpen: boolean;
  onDownload: ({
    packageUrl,
    checksumUrl,
    packageName,
    checksumName,
  }: DownloadParams) => Promise<void>;
  onClose: () => void;
  studyName: string;
}

export type DownloadModalProps = DownloadModalBaseProps & DownloadState;

/**
 * Modal for showing export progress and providing download link for study data.
 * @param {boolean} isOpen - Whether the modal is currently open
 * @param {function} onClose - Callback function to close the modal
 * @param {string} studyName - Name of the study being exported
 * @param {DownloadState} downloadState - State of the download process, including status, progress, and download URLs
 * @returns {React.JSX.Element}
 */
export default function DownloadModal({
  onDownload,
  isOpen,
  onClose,
  studyName,
  status,
  progress,
  jobKey,
  rawDownloadUrl,
  filename,
  checksumDownloadUrl,
  checksumFilename,
  errorMessage,
}: DownloadModalProps) {
  const isDownloadActive =
    status === DownloadStatus.PREPARING ||
    status === DownloadStatus.PROCESSING ||
    status === DownloadStatus.ACQUIRING ||
    status === DownloadStatus.RECONNECTING;
  const isDataFileAvailable =
    status === DownloadStatus.COMPLETED && !!rawDownloadUrl && !!filename;
  const isChecksumFileAvailable = !!checksumDownloadUrl && !!checksumFilename;

  const [closeConfirmation, setCloseConfirmation] = useState<
    'export' | 'download' | undefined
  >();
  const [downloadError, setDownloadError] = useState(false);

  // True when data files are actively being downloaded to the user's machine
  const [isDownloading, setIsDownloading] = useState(false);

  // True when data files have been successfully downloaded to the user's machine
  const [isDownloadReady, setIsDownloadReady] = useState(false);
  const downloadController = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) {
      setIsDownloading(false);
      setIsDownloadReady(false);
      setCloseConfirmation(undefined);
    }

    return () => {
      downloadController.current?.abort();
      downloadController.current = undefined;
    };
  }, [isOpen]);

  useEffect(() => setDownloadError(false), [rawDownloadUrl, isOpen]);

  // Prevent browser unload during active exports or downloads
  useEffect(() => {
    if (!isDownloadActive && !isDownloading) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      return true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDownloadActive, isDownloading]);

  const closeModal = () => {
    downloadController.current?.abort();
    downloadController.current = undefined;
    setIsDownloading(false);
    setIsDownloadReady(false);
    setCloseConfirmation(undefined);
    onClose();
  };

  const handleCloseAttempt = () => {
    if (isDownloading || isDownloadActive) {
      setCloseConfirmation(isDownloading ? 'download' : 'export');
    } else {
      closeModal();
    }
  };

  const handleConfirmClose = () => {
    closeModal();
  };

  const handleCancelClose = () => {
    setCloseConfirmation(undefined);
  };

  const handleDownloadClick = async () => {
    if (isDataFileAvailable) {
      const controller = new AbortController();

      downloadController.current = controller;
      setDownloadError(false);
      setIsDownloading(true);
      setIsDownloadReady(false);

      try {
        await onDownload({
          packageUrl: rawDownloadUrl,
          checksumUrl: checksumDownloadUrl,
          packageName: filename,
          checksumName: checksumFilename,
          signal: controller.signal,
        });

        if (
          downloadController.current === controller &&
          !controller.signal.aborted
        ) {
          setIsDownloadReady(true);
        }
      } catch (error) {
        if (
          downloadController.current === controller &&
          !controller.signal.aborted &&
          !(error instanceof Error && error.name === 'AbortError')
        ) {
          setDownloadError(true);
        }
      } finally {
        if (downloadController.current === controller) {
          downloadController.current = undefined;
          setIsDownloading(false);
        }
      }
    }
  };

  const renderDownloadStatusBanner = () => {
    if (isDownloading) {
      return (
        <Highlight
          variant="informational"
          iconStart={
            <IconBannersInfo
              color="$neutral.0"
              accentColor="$support.informational.default"
            />
          }
          title={i18n.t('Modal_Download.downloading_files_title')}
          body={i18n.t('Modal_Download.downloading_files_body')}
          testID="download-active-banner"
        />
      );
    } else if (isDownloadReady) {
      return (
        <Highlight
          variant="success"
          iconStart={
            <IconCheckCircle
              color="$neutral.0"
              accentColor="$support.positive.default"
            />
          }
          title={i18n.t('Modal_Download.download_ready_title')}
          body={i18n.t('Modal_Download.download_ready_body')}
          testID="download-ready-banner"
        />
      );
    }
  };

  const renderExportStatusBanner = () => {
    switch (status) {
      case DownloadStatus.ACQUIRING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.acquiring_title')}
            body={i18n.t('Modal_Download.acquiring_body')}
            testID="download-acquiring-banner"
          />
        );
      case DownloadStatus.FAILED:
        return (
          <Highlight
            variant="danger"
            iconStart={
              <IconBannersWarning
                color="$neutral.0"
                accentColor="$support.danger.default"
              />
            }
            title={i18n.t('Modal_Download.failed_title')}
            body={errorMessage || i18n.t('Modal_Download.error_generic_failed')}
            testID="download-failed-banner"
          />
        );
      case DownloadStatus.PREPARING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.preparing_title')}
            body={i18n.t('Modal_Download.active_body')}
            testID="download-preparing-banner"
          />
        );
      case DownloadStatus.PROCESSING:
      case DownloadStatus.RECONNECTING:
        return (
          <Highlight
            variant="informational"
            iconStart={
              <IconBannersInfo
                color="$neutral.0"
                accentColor="$support.informational.default"
              />
            }
            title={i18n.t('Modal_Download.processing_title')}
            body={i18n.t('Modal_Download.active_body')}
            testID="download-processing-banner"
          />
        );
      case DownloadStatus.COMPLETED:
        return (
          <Highlight
            variant="success"
            iconStart={
              <IconCheckCircle
                color="$neutral.0"
                accentColor="$support.positive.default"
              />
            }
            title={i18n.t('Modal_Download.completed_description')}
            body={i18n.t('Modal_Download.completed_body')}
            testID="download-completed-banner"
          />
        );
      default:
        return null;
    }
  };

  const renderDownloadSection = () => {
    return isDataFileAvailable ? (
      <VStack gap="$2">
        {!supportsFileDownload() ? (
          <Text type="$bodySmall.default">
            {i18n.t('Modal_Download.large_file_hint')}
          </Text>
        ) : null}
        {downloadError ? (
          <Highlight
            variant="danger"
            title={i18n.t('Modal_Download.transfer_failed')}
            body={i18n.t('Modal_Download.transfer_retry_body')}
            testID="download-transfer-error"
          />
        ) : null}
        <Text type="$bodyBase.strong">
          {i18n.t('Modal_Download.exported_file_label')}
        </Text>
        <Card>
          <HStack gap="$2" alignItems="center" justifyContent="space-between">
            <VStack gap="$1" flex={1}>
              <Text type="$bodySmall.default" testID="download-filename">
                {filename}
              </Text>
              {isChecksumFileAvailable ? (
                <Text
                  type="$bodySmall.default"
                  testID="download-checksum-filename"
                >
                  {checksumFilename}
                </Text>
              ) : null}
            </VStack>

            <IconButton
              variant="tertiary"
              aria-label={i18n.t(
                downloadError
                  ? 'Modal_Download.retry_download'
                  : 'Modal_Download.download_export_file',
              )}
              onPress={handleDownloadClick}
              disabled={isDownloading}
              testID="download-modal-download-button"
            >
              <IconDownload />
            </IconButton>
          </HStack>
        </Card>
      </VStack>
    ) : null;
  };

  return (
    <Dialog open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content" width="50%">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">{i18n.t('Modal_Download.title')}</Text>
            </Dialog.Title>
          </VStack>

          <ScrollView flex={1} minHeight={0} marginVertical="$4">
            <VStack gap="$2">
              <VStack gap="$1">
                <Text type="$bodyBase.strong">
                  {i18n.t('Modal_Download.study_label')}
                </Text>
                <Text type="$bodyBase.default" testID="download-study-name">
                  {studyName}
                </Text>
              </VStack>

              {isDownloading || isDownloadReady
                ? renderDownloadStatusBanner()
                : renderExportStatusBanner()}

              {jobKey && status === DownloadStatus.FAILED ? (
                <Text type="$bodySmall.default" testID="download-job-key">
                  {i18n.t('Modal_Download.job_key', { jobKey })}
                </Text>
              ) : null}

              {status !== DownloadStatus.IDLE && progress !== undefined ? (
                <VStack gap="$3" alignItems="center">
                  {status === DownloadStatus.PREPARING ||
                  status === DownloadStatus.ACQUIRING ||
                  isDownloading ? (
                    // Contain the rotating square so its corners cannot resize
                    // the scrollable area around the circular spinner.
                    <VStack
                      width={50}
                      height={50}
                      flexShrink={0}
                      overflow="hidden"
                      marginTop="$2"
                    >
                      <LoadingSpinner testID="download-loading" />
                    </VStack>
                  ) : null}

                  {status !== DownloadStatus.ACQUIRING && !isDownloading ? (
                    <VStack gap="$2" width="100%">
                      <ProgressBar
                        progress={progress}
                        color={
                          status === DownloadStatus.FAILED
                            ? '$neutral.40'
                            : '$abbott.lightBlue.default'
                        }
                        testId="download-progress-bar"
                      />
                      <Text
                        textAlign="center"
                        type="$bodySmall.default"
                        testID="download-progress-text"
                      >
                        {`${Math.round(progress)}${i18n.t('Modal_Download.progress_complete')}`}
                      </Text>
                    </VStack>
                  ) : null}
                </VStack>
              ) : null}

              {renderDownloadSection()}
            </VStack>
          </ScrollView>
          <HStack justifyContent="flex-end" gap="$3" flexShrink={0}>
            <Button
              variant="secondary"
              size="medium"
              label={
                status === DownloadStatus.FAILED ||
                status === DownloadStatus.COMPLETED
                  ? i18n.t('Common.close')
                  : i18n.t('Common.cancel')
              }
              onPress={handleCloseAttempt}
              testID="download-modal-close-button"
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>

      <ConfirmationModal
        isOpen={!!closeConfirmation}
        title={i18n.t('Modal_Download.confirm_close_title')}
        description={
          closeConfirmation
            ? i18n.t(`Modal_Download.confirm_close_${closeConfirmation}_body`)
            : ''
        }
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        showCancelButton={true}
      />
    </Dialog>
  );
}
